import { SceneService } from '@src/services/SceneService';
import { isSourceValueApproxEqual } from '@src/utility/approximateEquality';

import type { KaraokeConfig } from '../../../editor/models/karaoke/KaraokeConfig';
import type { RendererState } from '@src/renderer/RendererState';
import type { StateChangeContext } from '@src/stores/lib/StateChangeObserver';
import type VideoCreatorStore from '@src/stores/VideoCreatorStore';

// Restrict to string keys so TypeScript doesn't widen to number | string
const manualChangeFields: Array<Extract<keyof KaraokeConfig, string>> = [
  'width',
  'x',
  'y',
];

// Track the previous deferred callback so we can cancel it before registering
// a new one. This prevents stale callbacks from accumulating during rapid drags
// and writing outdated position values.
let cancelPreviousDeferred: (() => void) | null = null;

function detectAndApplyManualChangesToKaraokeConfig(
  videoCreator: VideoCreatorStore,
  newState: RendererState,
  context: StateChangeContext,
): boolean {
  // Only detect manual changes when the user is interacting with the renderer
  // iframe (canvas drag). Slider-driven changes update the config directly and
  // should not be re-applied from the renderer echo.
  if (!context.isManualInteraction) return false;

  const activeElementOld = videoCreator.getActiveElement();
  if (!activeElementOld) {
    return false;
  }
  const activeElementNew = newState.elements.find(
    el => el.source.id === activeElementOld.source.id,
  );
  if (!activeElementNew) {
    return false;
  }

  let activeClip;
  let newKaraokeConfig: KaraokeConfig | undefined;

  activeClip = videoCreator.rootStore.karaokeStore.getKaraokeLineById(
    activeElementNew.source.id,
  );
  const karaokeConfigId = activeClip?.karaokeConfigId;
  if (!karaokeConfigId) return false; // Early return as no config avaialbe to update

  newKaraokeConfig =
    videoCreator.rootStore.karaokeStore.karaokeConfigs.get(karaokeConfigId);
  if (!newKaraokeConfig) return false;

  const propertyMap = manualChangeFields.reduce((acc, field) => {
    // no change — use approximate comparison to absorb renderer round-trip
    // floating-point noise (e.g. "54%" echoed back as "53.99999999%")
    if (
      isSourceValueApproxEqual(
        activeElementNew.source[field],
        activeElementOld.source[field],
      )
    ) {
      return acc;
    }

    acc[field] = activeElementNew.source[field];

    return acc;
  }, {} as Partial<KaraokeConfig>);

  if (Object.keys(propertyMap).length) {
    const cb = () => {
      // Try to find the scene for this karaoke element
      const editor = videoCreator.getEditorStore().editor;
      const timeline = editor.timeline;
      const sceneService = new SceneService();
      const scene = sceneService.findSceneForElement(
        timeline,
        activeElementNew.source.id,
      );
      // Scene update is handled at RendererSyncService when scene has a per-scene
      // override. If no override exists, fall through to update global config.
      if (scene?.layout.bindings.karaoke) {
        const hasOverride =
          !!scene.layout.bindings.karaoke.transformKeyframes?.[0]?.textPosition;
        if (hasOverride) {
          return; // scene has per-scene override: defer to RendererSyncService
        }
        // no override on this scene: fall through to update global KaraokeConfig
        // RendererSyncService won't create a new override (gated in buildKeyframeFromElement)
      }

      if (newKaraokeConfig) {
        videoCreator.textBrandingService.checkPropsChanged(
          'karaoke',
          propertyMap,
        );
        const newConfig = { ...newKaraokeConfig, ...propertyMap };
        editor.commandUpdateKaraokeConfig(newConfig);
      }
    };

    // Cancel any previously deferred callback to prevent stale values from
    // accumulating. Only the latest drag position should be applied.
    if (cancelPreviousDeferred) {
      cancelPreviousDeferred();
      cancelPreviousDeferred = null;
    }

    if (videoCreator.rootStore.videoSourceStore.hasDebouncedSourceRefresh) {
      cancelPreviousDeferred =
        videoCreator.rootStore.videoSourceStore.onNextSourceRefresh(cb);
    } else {
      cb();
    }
  }

  return false;
}

detectAndApplyManualChangesToKaraokeConfig.displayName =
  'detectAndApplyManualChangesToKaraokeConfig';

export default detectAndApplyManualChangesToKaraokeConfig;
